import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/components/DatePicker";

// Shared thread view. mine=true renders from the employee's perspective.
export const ReplyThread = ({ messages, mine, onSend, busy, placeholder, testPrefix = "thread" }) => {
  const [text, setText] = useState("");
  const endRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages?.length]);
  const send = async () => { if (!text.trim()) return; await onSend(text.trim()); setText(""); };
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-thread`}>
      <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
        {(messages || []).length === 0 && <li className="text-xs text-muted-foreground text-center py-3 flex items-center justify-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> No replies yet.</li>}
        {(messages || []).map((m) => { const own = mine ? m.author_role === "employee" : m.author_role !== "employee"; return (
          <li key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`${testPrefix}-msg-${m.id}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${own ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              <div className={`text-[10px] mt-1 ${own ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.author} · {fmtDateTime(m.created_at)}</div>
            </div>
          </li>); })}
        <li ref={endRef} />
      </ul>
      <div className="flex gap-2 items-end">
        <Textarea data-testid={`${testPrefix}-input`} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="text-sm" onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} onClick={(e) => e.stopPropagation()} />
        <Button size="sm" data-testid={`${testPrefix}-send`} disabled={busy || !text.trim()} onClick={(e) => { e.stopPropagation(); send(); }}><Send className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
};

export const useThreadSender = (url, reload) => {
  const [busy, setBusy] = useState(false);
  const send = async (text) => { setBusy(true); try { await api.post(url, { text }); reload?.(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  return { send, busy };
};
